import { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { createConsumer } from '@rails/actioncable';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

const ACCEPTED_FILE_TYPES = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Microsoft Word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Microsoft Excel',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Powerpoint'
};

const ACCEPTED_MIME_TYPES = Object.keys(ACCEPTED_FILE_TYPES);
export const ACCEPTED_TYPE_NAMES = Object.values(ACCEPTED_FILE_TYPES);

type UploadStatus = 'idle' | 'uploading' | 'processing' | 'success' | 'upload_error' | 'processing_error';

type UploadResponse = {
  upload_id: number;
};

type ProcessedResponse = {
  llmData: { [key: string]: string };
  propertyFormData: { [key: string]: string };
  populatedFieldNames: string[];
  inferredFields: string[];
  uploadMetadata?: {
    filename?: string;
    uploadId?: number;
    aiModel?: string;
  };
};

type ProcessingErrorResponse = {
  error: { code: string; message: string };
} | null;

type UploadErrorResponse = Error | null;

export type UseListingDocumentUploadReturn = {
  // Dropzone props
  getRootProps: () => any;
  getInputProps: (props?: any) => any;
  isDragActive: boolean;

  // State
  status: UploadStatus;
  fileDropError: string;
  uploadError: UploadErrorResponse;
  processingError: ProcessingErrorResponse;
  selectedFile: File | null;

  // Computed values
  isUploading: boolean;
  progressText: string;

  // Actions
  resetUpload: () => void;
};

export function useListingDocumentUpload(
  disabled: boolean = false,
  onProcessingComplete?: (data: any) => void
): UseListingDocumentUploadReturn {
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [uploadUUID, setUploadUUID] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // NOTE: use the `handleXXXError` helper functions to set these errors along with status updates
  const [fileDropError, setFileDropError] = useState<string>('');
  const [uploadError, setUploadError] = useState<UploadErrorResponse>(null);
  const [processingError, setProcessingError] = useState<ProcessingErrorResponse>(null);

  const consumerRef = useRef<any>(null);
  const subscriptionRef = useRef<any>(null);
  const onProcessingCompleteRef = useRef(onProcessingComplete);

  // Keep ref updated with latest callback so the websocket-management useEffect can always call the latest without
  // making the function a dep, which would cause infinite render loops if useCallback wasn't used in the right places
  // NOTE: using a ref to bypass useEffect's dep array may be an anti-pattern, but refactoring would be a larger effort.
  useEffect(() => {
    onProcessingCompleteRef.current = onProcessingComplete;
  }, [onProcessingComplete]);

  // Delayed auto-clear of fileDropError
  useEffect(() => {
    if (!fileDropError) return;

    const timeoutId = setTimeout(() => {
      setFileDropError('');
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [fileDropError]);

  const acceptingDrops = status === 'idle';
  const isUploading = status === 'uploading' || status === 'processing';
  const progressText: string = (() => {
    switch (status) {
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Processing...';
      default:
        return '';
    }
  })();

  // Helper functions to encapsulate error setters with status updates (avoids infinite render loop)
  // TODO: Important: after processing is complete - there still seems to be an infinite render loop issue
  const handleUploadError = (error: Error) => {
    setUploadError(error);
    setStatus('upload_error');
  };

  const handleProcessingError = (error: ProcessingErrorResponse) => {
    setProcessingError(error);
    setStatus('processing_error');
  };

  const handleFileDropError = (error: string) => {
    setFileDropError(error);
    // Keep status as 'idle' so dropzone continues accepting drops
  };
  // End Helper functions

  const onUploadDocSelected = (file: File) => {
    const newUUID = uuid();
    setUploadUUID(newUUID);
    setSelectedFile(file);
    setStatus('uploading');
  };

  // websocket management
  useEffect(() => {
    if (!uploadUUID || !selectedFile) return;

    if (!consumerRef.current) {
      consumerRef.current = createConsumer();
    }
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }
    subscriptionRef.current = consumerRef.current.subscriptions.create(
      {
        channel: 'Projects::ListingDocUploadChannel',
        upload_uuid: uploadUUID,
      },
      {
        connected: async () => {
          // Only upload after socket is connected to avoid missing messages
          try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('upload_uuid', uploadUUID);

            await axios.post<UploadResponse>('/property_analyze_doc/upload',
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            setStatus('processing');
          } catch (err) {
            handleUploadError(err as Error);
          }
        },
        disconnected: () => {},
        received: (payload: any) => {
          if (payload.status === 'error') {
            handleProcessingError(payload.error);
          } else {
            const processedData = payload.data as ProcessedResponse;
            setStatus('success');

            // Call the callback directly when processing completes
            if (onProcessingCompleteRef.current) {
              onProcessingCompleteRef.current(processedData);
            }
          }

          // also forces an unsubscribe & disconnect
          setUploadUUID(null);
        }
      }
    );

    // Cleanup
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect();
        consumerRef.current = null;
      }
    };
  }, [uploadUUID, selectedFile]);

  const onDrop = (acceptedFiles: File[]) => {
    if (!acceptingDrops) return;

    // Clear any existing file drop error when a new file is dropped
    if (fileDropError) {
      setFileDropError('');
    }

    if (acceptedFiles) {
      if (acceptedFiles.length === 1) {
        onUploadDocSelected(acceptedFiles[0]);
      } else {
        handleFileDropError('Only 1 file can be uploaded');
        return;
      }
    }
  };

  const onDropRejected = (rejectedFiles: any[]) => {
    if (rejectedFiles && rejectedFiles.length > 0) {
      handleFileDropError(`Invalid file type.`);
    }
  };

  const resetUpload = () => {
    setStatus('idle');
    setFileDropError('');
    setUploadError(null);
    setProcessingError(null);
    setSelectedFile(null);
    setUploadUUID(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    disabled: disabled || !acceptingDrops,
    accept: ACCEPTED_MIME_TYPES
  });

  return {
    getRootProps,
    getInputProps,
    isDragActive: isDragActive && acceptingDrops,
    status,
    fileDropError,
    uploadError,
    processingError,
    selectedFile,
    isUploading,
    progressText,
    resetUpload,
  };
}
